/**
 * Модель хронологии севооборота — чистые функции над ответом сервера.
 *
 * Вынесено из компонента, потому что здесь живут два утверждения, которые
 * обязаны проверяться тестом, а не глазами:
 *
 * 1. **площади занятостей не складываются**. Хлопок 10 га и посеянная после
 *    его уборки пшеница 10 га — это те же 10 га земли, а не 20. Поэтому здесь
 *    нет ни одной функции «сумма по треку»: сложить их можно только в одном
 *    смысле — «сколько засеяно за цикл», и эту величину считает сервер и
 *    называет `sown_microha`, а не `total_microha`;
 * 2. **окно бывает планом, а бывает фактом**, и это разные утверждения. Пока
 *    работ кампании не было, действует нормативное окно из профиля культуры —
 *    объявленное ДОПУЩЕНИЕ. Первая работа открывает фактическое начало,
 *    последняя закрывает конец. Показывать оба состояния одинаково значило бы
 *    выдавать догадку за отчёт.
 */

import type { CropOccupancyOut } from "../../api/client";
import { defineMessages, type Message } from "../../i18n/define";
import { tr } from "../../i18n/runtime";

/** Чем задано показанное окно занятости. */
export type WindowBasis =
  /** Обе границы плановые: работ ещё не было. */
  | "planned"
  /** Обе границы фактические: цикл открыт и закрыт работами. */
  | "actual"
  /** Начало фактическое, конец ещё плановый — цикл идёт. */
  | "mixed";

export function windowBasis(row: CropOccupancyOut): WindowBasis {
  if (row.actual_from && row.actual_to) return "actual";
  if (row.actual_from || row.actual_to) return "mixed";
  return "planned";
}

const BASIS_LABEL: Record<WindowBasis, Message> = defineMessages({
  planned: { id: "placement.timeline.basis.planned", defaultMessage: "плановое окно (допущение)", description: "Пометка под окном занятости в севообороте (демо): обе границы плановые, это допущение, не факт; строчная, под датами" },
  actual: { id: "placement.timeline.basis.actual", defaultMessage: "фактическое окно (по работам)", description: "Пометка под окном занятости в севообороте: границы определены по фактическим работам; строчная, под датами" },
  mixed: { id: "placement.timeline.basis.mixed", defaultMessage: "начало по работе, конец плановый", description: "Пометка под окном занятости в севообороте: начало подтверждено работой, конец ещё плановый; строчная, под датами" },
});

export function windowBasisLabel(row: CropOccupancyOut): string {
  return tr(BASIS_LABEL[windowBasis(row)]);
}

/** Одна единица землепользования и её цепочка занятостей за год цикла. */
export interface TimelineTrack {
  key: string;
  contourId: number;
  contourNo: string;
  partId: number | null;
  partLabel: string | null;
  /** Фазовая группа хозяйства: `A` | `B` | null, если единица вне групп. */
  rotationPhase: string | null;
  rows: CropOccupancyOut[];
}

/**
 * Разложить строки по единицам землепользования, сохранив порядок сервера.
 *
 * Ключ — контур И часть: у нарезанного контура части живут своими
 * севооборотами, и склеивать их значило бы утверждать общий цикл там, где его
 * нет. Порядок треков — порядок первого появления: сервер уже отсортировал
 * строки по началу окна, и пересортировывать их здесь значило бы завести второй
 * порядок, который однажды разойдётся с первым.
 */
export function groupByUnit(
  rows: ReadonlyArray<CropOccupancyOut>,
): TimelineTrack[] {
  const tracks = new Map<string, TimelineTrack>();
  for (const row of rows) {
    const key = `${row.contour_id}:${row.part_id ?? ""}`;
    const track = tracks.get(key);
    if (track) {
      track.rows.push(row);
      continue;
    }
    tracks.set(key, {
      key,
      contourId: row.contour_id,
      contourNo: row.contour_no,
      partId: row.part_id ?? null,
      partLabel: row.part_label ?? null,
      rotationPhase: row.rotation_phase ?? null,
      rows: [row],
    });
  }
  return [...tracks.values()];
}

/**
 * Занятости, действующие на дату среза. Их может быть НЕСКОЛЬКО (две культуры
 * делят площадь контура) и может не быть ни одной (зимний перерыв). Пустой
 * список — это ответ «сейчас на земле ничего не заявлено», а не отсутствие
 * данных, и подменять его последней известной культурой нельзя.
 */
export function currentRows(
  rows: ReadonlyArray<CropOccupancyOut>,
): CropOccupancyOut[] {
  return rows.filter((row) => row.is_current);
}
